import React, { useState, useEffect, useCallback, useContext } from "react";

import searchApiService from "../service/searchApiService";
import { useConfig } from "./useConfig";
import { dispatchGA4SearchEvent } from "../utils/events";

const SearchResultsContext = React.createContext();

export const SearchResultsProvider = ({ children }) => {
  const { customParams } = useConfig();

  const [data, setData] = useState();
  const [error, setError] = useState();
  const [isLoading, setLoading] = useState(false);
  const [searchWord, setSearchWord] = useState("");

  const handleError = (error) => {
    console.error(error);
    setError(true);
    setLoading(false);

    dispatchGA4SearchEvent({
      eventAction: "api_error",
      eventLabel: searchWord,
    });
  };

  const runQuery = useCallback(
    async (uuid, word, page, query, anchors, cacheKey, sortKey) => {
      if (anchors && !word) word = " ";
      if (
        !uuid ||
        !word ||
        !query ||
        cacheKey === "&cacheKey=" ||
        sortKey === "&sortKey=" ||
        cacheKey === "&cacheKey=undefined" ||
        cacheKey === "&cacheKey=null"
      )
        return;

      setSearchWord(word);
      setLoading(true);

      const handleSuccess = (res) => {
        setError(false);
        setData(res?.data);
        setLoading(false);

        dispatchGA4SearchEvent({
          eventAction: "results",
          eventCount: res?.data?.meta?.results_count,
          eventProducts: res?.data?.results,
          eventLabel:
            res?.data?.meta?.term ||
            res?.data?.meta?.anchors_applied
              ?.map(
                ({ name, tree_key }) => `${name} (${tree_key?.split(":")[0]})`
              )
              ?.join(", "),
        });

        return res?.data;
      };

      const normalizedQuery = query.map(({ facet, values, name }) => {
        if (typeof values === "string") {
          const newValues = [values];
          return { facet: facet || name, values: newValues };
        }
        return { facet: facet || name, values };
      });

      let userCustomParams = "";
      Object.entries(customParams)?.map(
        ([paramKey, paramValue]) =>
          (userCustomParams = userCustomParams + `&${paramKey}=${paramValue}`)
      );

      const fetchApi = async () =>
        await searchApiService
          .getSearchResults(
            uuid,
            word,
            page,
            normalizedQuery,
            anchors,
            cacheKey,
            sortKey,
            userCustomParams
          )
          .then(handleSuccess)
          .catch(handleError);

      // eslint-disable-next-line react-hooks/exhaustive-deps
      const results = await fetchApi();
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
      return results;
    },
    [customParams]
  );

  useEffect(() => {
    runQuery();
  }, [runQuery]);

  return (
    <SearchResultsContext.Provider
      value={{
        data,
        isLoading,
        error,
        refetch: runQuery,
        setLoading,
      }}
    >
      {children}
    </SearchResultsContext.Provider>
  );
};

export const useSearchResults = () => {
  const { data, isLoading, error, refetch, setLoading } =
    useContext(SearchResultsContext);
  return { data, isLoading, error, refetch, setLoading };
};
